(() => {
  'use strict';
  const storageKey = 'vous:gift-draft:v4';
  const validId = id => /^\d+$/.test(String(id)) && Number.isSafeInteger(Number(id)) && Number(id) > 0;
  const eligible = item => validId(item.variant_id) && Number.isSafeInteger(item.quantity) && item.quantity > 0 && typeof item.key === 'string' && item.key.length > 0 && !Object.keys(item.properties || {}).length && !item.selling_plan_allocation && !item.selling_plan && !item.parent_relationship && !item.item_components?.length;
  function quantities(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const result = {};
    for (const [id, quantity] of Object.entries(value)) {
      if (!validId(id) || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
      result[id] = quantity;
    }
    if (!Number.isSafeInteger(Object.values(result).reduce((sum, quantity) => sum + quantity, 0))) return null;
    return result;
  }
  function sourceBounds(source) {
    if (source?.type !== 'cart' || !Array.isArray(source.lines) || !source.lines.length) return null;
    const bounds = {}, seen = new Set();
    for (const line of source.lines) {
      if (!line || typeof line.key !== 'string' || !line.key || seen.has(line.key) || !validId(line.variantId) || !Number.isSafeInteger(line.originalQuantity) || line.originalQuantity <= 0) return null;
      seen.add(line.key);
      const id = String(line.variantId);
      bounds[id] = (bounds[id] || 0) + line.originalQuantity;
      if (!Number.isSafeInteger(bounds[id])) return null;
    }
    return bounds;
  }
  function withinSource(selection, source) {
    const normalized = quantities(selection), bounds = sourceBounds(source);
    return Boolean(normalized && bounds && Object.entries(normalized).every(([id, quantity]) => quantity <= (bounds[id] || 0)));
  }
  function allocate(selection, source) {
    if (!withinSource(selection, source)) throw new Error('source_changed');
    const remaining = { ...selection };
    return source.lines.flatMap(line => {
      const id = String(line.variantId), quantity = Math.min(remaining[id] || 0, line.originalQuantity);
      remaining[id] = (remaining[id] || 0) - quantity;
      return quantity ? [{ key: line.key, variantId: id, quantity, originalQuantity: line.originalQuantity }] : [];
    });
  }
  function sourceMatches(cart, source) {
    if (!Array.isArray(cart?.items) || !sourceBounds(source)) return false;
    return source.lines.every(saved => {
      const item = cart.items.find(line => line.key === saved.key);
      return item && eligible(item) && String(item.variant_id) === String(saved.variantId) && item.quantity === saved.originalQuantity;
    });
  }
  function handoff(cart, allowedIds, previous, returnUrl) {
    if (!Array.isArray(cart?.items)) throw new Error('cart');
    const allowed = new Set(allowedIds.map(String)), selection = {}, lines = [];
    for (const item of cart.items) {
      if (!allowed.has(String(item.variant_id)) || !eligible(item)) continue;
      const id = String(item.variant_id);
      selection[id] = (selection[id] || 0) + item.quantity;
      if (!Number.isSafeInteger(selection[id])) throw new Error('cart');
      lines.push({ key: item.key, variantId: id, quantity: item.quantity, originalQuantity: item.quantity });
    }
    if (!lines.length) throw new Error('cart');
    const old = previous?.version === 4 && previous.source?.type === 'cart' ? previous.state : null;
    const state = { quantities: selection, to: '', from: '', message: '', mode: 'written' };
    for (const field of ['to', 'from', 'message']) if (typeof old?.[field] === 'string') state[field] = old[field];
    if (['written', 'blank', 'none'].includes(old?.mode)) state.mode = old.mode;
    return { version: 4, giftId: crypto.randomUUID(), state, source: { type: 'cart', returnUrl, lines, originalQuantities: { ...selection } }, stage: 'personalize' };
  }
  window.VousCartGiftJourney = Object.freeze({ storageKey, eligible, quantities, sourceBounds, withinSource, allocate, sourceMatches, handoff });
  document.addEventListener('change', async event => {
    if (!event.target.matches('[data-send-gift]') || !event.target.checked) return;
    const box = event.target.closest('[data-gift-opt-in]'), page = box?.closest('[data-cart-page]');
    if (!page || box.dataset.source !== 'cart') { event.target.checked = false; return; }
    const error = box.querySelector('[data-handoff-error]');
    const t = JSON.parse(box.querySelector('[data-handoff-strings]').textContent);
    error.hidden = true;
    event.target.disabled = true;
    try {
      if (page.getAttribute('aria-busy') === 'true' || [...page.querySelectorAll('[data-line-quantity]')].some(input => input.value !== input.defaultValue)) throw new Error(t.unsaved);
      const allowed = JSON.parse(page.querySelector('[data-cart-gift-variants]').textContent);
      const response = await fetch((window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(t.cart);
      let previous;
      try { previous = JSON.parse(sessionStorage.getItem(storageKey)); } catch { previous = null; }
      const draft = handoff(await response.json(), allowed, previous, location.pathname + location.search);
      try { sessionStorage.setItem(storageKey, JSON.stringify(draft)); } catch { throw new Error(t.storage); }
      event.target.checked = false;
      location.assign(box.dataset.giftUrl);
    } catch (failure) {
      error.textContent = failure.message === 'cart' ? t.cart : failure.message || t.storage;
      error.hidden = false;
      event.target.checked = false;
    } finally { event.target.disabled = false; }
  });
  const resetCheckbox = () => document.querySelectorAll('[data-send-gift]').forEach(input => { input.checked = false; input.disabled = false; });
  window.addEventListener('pageshow', resetCheckbox);
  resetCheckbox();
})();
